import type { GitSidebarCategory } from '@readme/api-contracts/schemas/sidebar';
import type { NextPageLink } from '@readme/backend/models/page/types';
import type { SuperHubRouteParams } from '@routes/SuperHub/types';

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import useReadmeApi from '@core/hooks/useReadmeApi';
import useUniqueId from '@core/hooks/useUniqueId';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { RHFGroup } from '@ui/RHF';
import { createSuspenseDataBoundary } from '@ui/SuspenseDataBoundary/createSuspenseDataBoundary';
import WhatsNext from '@ui/WhatsNext';

import { useSuperHubEditorFormContext } from '../../Context';

import { flattenGitSidebarPagesToMongo, normalizeMongoPagesToGit, normalizeGitPagesToMongo } from './util';

export interface WhatsNextData {
  guidesSidebar: GitSidebarCategory[];
  referenceSidebar: GitSidebarCategory[];
}

/**
 * Suspense boundary for loading the sidebars needed by What's Next.
 */
export const WhatsNextDataBoundary = createSuspenseDataBoundary<WhatsNextData>({
  name: 'SuperHubEditorWhatsNext',
  useBoundaryData: () => {
    const { section } = useParams<SuperHubRouteParams>();
    const [getApiBaseUrl, currentSidebar, defaultApiOptions] = useSuperHubStore(s => [
      s.getApiBaseUrl,
      s.sidebar.data,
      s.defaultApiOptions,
    ]);

    const apiBaseUrl = getApiBaseUrl();
    const guidesSidebarFromStore = section === 'docs' ? currentSidebar : null;
    const referenceSidebarFromStore = section === 'reference' ? currentSidebar : null;

    // What's Next is optional, so only fetch the sidebar that's not already in the store.
    const guidesUrl = guidesSidebarFromStore || !apiBaseUrl ? null : `${apiBaseUrl}/sidebar?page_type=guide`;
    const referenceUrl = referenceSidebarFromStore || !apiBaseUrl ? null : `${apiBaseUrl}/sidebar?page_type=reference`;

    const options = {
      ...defaultApiOptions,
      swr: {
        revalidateOnMount: true,
        revalidateOnFocus: false,
        shouldRetryOnError: true,
        suspense: true,
      },
    } as const;

    const { data: guidesSidebarData } = useReadmeApi<GitSidebarCategory[]>(guidesUrl, options);
    const { data: referenceSidebarData } = useReadmeApi<GitSidebarCategory[]>(referenceUrl, options);

    return {
      guidesSidebar: guidesSidebarFromStore || guidesSidebarData || [],
      referenceSidebar: referenceSidebarFromStore || referenceSidebarData || [],
    };
  },
});

export default function WhatsNextEditor({ guidesSidebar, referenceSidebar }: WhatsNextData) {
  const uid = useUniqueId('WhatsNextEditor');
  const whatsNextLabel = useProjectStore(s => s.data.appearance.whats_next_label);
  const { control } = useSuperHubEditorFormContext();

  /**
   * Flat list of all guides AND reference pages to allow users to pick from.
   */
  const internalPages = useMemo(() => {
    const mergedData = [...(guidesSidebar ?? []), ...(referenceSidebar ?? [])];
    return mergedData.reduce<NextPageLink[]>((result, category) => {
      return [...result, ...flattenGitSidebarPagesToMongo(category.pages, category.title)];
    }, []);
  }, [guidesSidebar, referenceSidebar]);

  return (
    <RHFGroup control={control} id={uid('content-next')} name="content.next">
      {({ field }) => (
        <WhatsNext
          description={field.value?.description ?? undefined}
          internalPages={internalPages}
          isEditable
          label={whatsNextLabel ?? undefined}
          onChange={event => {
            field.onChange({
              ...field.value,
              description: event.target.value,
            });
          }}
          onPagesUpdate={nextPages => {
            field.onChange({
              ...field.value,
              pages: normalizeMongoPagesToGit(nextPages),
            });
          }}
          pages={normalizeGitPagesToMongo(field.value?.pages ?? [])}
        />
      )}
    </RHFGroup>
  );
}

export * from './util';
